import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { hostIps } from '../validation.ts';
import {
  errorText,
  formatError,
  result,
  stringValue,
  validation,
  validationError,
} from './shared.ts';

function isPublicIp(value: string): boolean {
  if (/^(?:10|127)\./.test(value)) {
    return false;
  }
  if (/^192\.168\./.test(value)) {
    return false;
  }
  if (/^172\.(?:1[6-9]|2\d|3[01])\./.test(value)) {
    return false;
  }
  if (/^(?:0\.0\.0\.0|::1|fc|fd|fe80)/i.test(value)) {
    return false;
  }
  return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(value) || value.includes(':');
}

async function collect(): Promise<RawData> {
  const endpoint = 'https://ip.betandcalm.com/';
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  let response: Response;
  try {
    response = await fetch(endpoint, {
      cache: 'no-store',
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Public IP request timed out after 5 seconds', {
        cause: error,
      });
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) {
    throw new Error(`Public IP endpoint returned ${response.status}`);
  }
  const responseText = await response.text();
  if (!isPublicIp(responseText.trim())) {
    throw new Error('Public IP endpoint returned an invalid address');
  }
  return {
    responseText,
  };
}

function validatePublicIp(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const responseText = stringValue(rawData, 'responseText');
  const ip = responseText?.trim() ?? null;
  if (!ip || !isPublicIp(ip)) {
    reasons.push('Public IP endpoint did not report a public address');
  }
  const ips = hostIps(context);
  const directIp = ips?.directIp.trim() ?? null;
  const expected = ips?.tcpIp.trim() ?? null;
  if (expected && expected != ip) {
    reasons.push(`Public IP does not match hostParams.ips.tcpIp=${expected}`);
  }
  if (directIp && expected && directIp == expected) {
    reasons.push(
      'hostParams.ips.directIp must differ from hostParams.ips.tcpIp',
    );
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const ip = String(rawData.responseText).trim();
  return ip.length <= 32 ? ip : 'IPv6 address';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validatePublicIp(rawData, context);
}

export const publicIpTest: TestDefinition = {
  id: 'publicIp',
  title: 'Public IP',
  category: 'stableProfile',
  spoofingChromeArgs: ['proxy-server'],
  format,
  run,
  validate,
};
